import { Button } from "@admin/components/atoms/Button";
import useClickOutside from "@admin/hooks/useClickOutside";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface MonthPickerProps {
  setSelectedMonth: Dispatch<SetStateAction<Date | undefined>>;
  menuOrientation?: "left" | "right";
}

export function MonthPicker({
  setSelectedMonth,
  menuOrientation = "right",
}: MonthPickerProps) {
  const { t, i18n } = useTranslation();
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedMonth, setSelectedMonthState] = useState<Date | undefined>(
    new Date(),
  );

  const [displayYear, setDisplayYear] = useState<number>(
    new Date().getFullYear(),
  );

  const monthPickerRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(monthPickerRef, () => setShowMonthPicker(false));

  const months = [
    t("shift.January"),
    t("shift.February"),
    t("shift.March"),
    t("shift.April"),
    t("shift.May"),
    t("shift.June"),
    t("shift.July"),
    t("shift.August"),
    t("shift.September"),
    t("shift.October"),
    t("shift.November"),
    t("shift.December"),
  ];

  const now = new Date();
  const isCurrentMonth =
    selectedMonth &&
    selectedMonth.getFullYear() === now.getFullYear() &&
    selectedMonth.getMonth() === now.getMonth();
  const handleMonthSelect = (monthIndex: number) => {
    let targetYear = displayYear;
    let targetMonth = monthIndex;

    if (targetMonth < 0) {
      targetMonth = 11;
      targetYear--;
    } else if (targetMonth > 11) {
      targetMonth = 0;
      targetYear++;
    }

    const newDate = new Date(targetYear, targetMonth, 1);

    const now = new Date();
    const isAfterCurrentMonth =
      newDate.getFullYear() > now.getFullYear() ||
      (newDate.getFullYear() === now.getFullYear() &&
        newDate.getMonth() > now.getMonth());

    if (isAfterCurrentMonth) {
      return;
    }

    setDisplayYear(targetYear);
    setSelectedMonthState(newDate);
    setSelectedMonth(newDate);
    setShowMonthPicker(false);
  };

  const goToPreviousYear = () => {
    setDisplayYear((prevYear) => prevYear - 1);
  };

  const goToNextYear = () => {
    setDisplayYear((prevYear) => prevYear + 1);
  };

  return (
    <div className="relative">
      <div className="flex flex-row gap-2 w-fit items-center">
        <Button
          size="xs"
          icon
          className="!bg-gray-300 flex justify-center !text-black"
          onClick={() =>
            handleMonthSelect((selectedMonth?.getMonth() as number) - 1)
          }
        >
          <i className="ki-filled ki-left"></i>
        </Button>
        <div
          onClick={() => {
            setShowMonthPicker((prev) => !prev);
          }}
          className="select flex items-center select-sm pr-12 gap-2 text-gray-700 rounded-md bg-light border border-gray-300 cursor-pointer"
        >
          <span>{t("shift.Month")}:</span>
          <span>
            {selectedMonth?.toLocaleDateString(i18n.language, {
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>
        <Button
          size="xs"
          icon
          className="!bg-gray-300 flex justify-center !text-black"
          disabled={isCurrentMonth}
          onClick={() =>
            handleMonthSelect((selectedMonth?.getMonth() as number) + 1)
          }
        >
          <i className="ki-filled ki-right"></i>
        </Button>
      </div>

      {showMonthPicker && (
        <div
          ref={monthPickerRef}
          className={`card absolute z-30 bg-white p-4 ${
            menuOrientation === "right" ? "left-0" : "right-0"
          } w-[20rem]`}
        >
          <div className="flex justify-between items-center mb-2">
            <button onClick={goToPreviousYear}>
              <i className="ki-filled ki-left text-xl font-bold text-blue-500" />
            </button>
            <span className="font-semibold">{displayYear}</span>
            <button onClick={goToNextYear}>
              <i className="ki-filled ki-right text-xl font-bold text-blue-500" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {months.map((monthName, index) => {
              const date = new Date(displayYear, index, 1);

              const isBefore2024 = date.getFullYear() < 2024;
              const currentYear = new Date().getFullYear();
              const currentMonth = new Date().getMonth();
              const isAfterCurrentMonth =
                date.getFullYear() > currentYear ||
                (date.getFullYear() === currentYear &&
                  date.getMonth() > currentMonth);

              const isDisabled = isBefore2024 || isAfterCurrentMonth;

              const isSelected =
                selectedMonth &&
                selectedMonth.getFullYear() === displayYear &&
                selectedMonth.getMonth() === index;

              return (
                <button
                  key={monthName}
                  disabled={isDisabled}
                  className={`text-sm font-semibold rounded-md p-2 transition-colors duration-200 ${
                    isSelected
                      ? "bg-blue-500 text-white"
                      : isDisabled
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-700 hover:bg-blue-400 hover:text-white"
                  }`}
                  onClick={() => handleMonthSelect(index)}
                >
                  {monthName}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
